// Existencias por bodega + ajuste a valor exacto. Port de views/inventario.js.
import { useState, useEffect } from 'react';
import { useBodegas } from '../hooks/useBodegas';
import { idBodegaPrincipal } from '../lib/api/bodegas';
import { useInventario, useAjustarStock } from '../hooks/useInventario';
import { DataTable, type Column } from '../components/DataTable';
import { NivelBadge } from '../components/Badge';
import { Thumb } from '../components/Thumb';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';
import { SkeletonTable } from '../components/Skeletons';
import { ErrorBox } from '../components/ErrorBox';
import { useToast } from '../components/Toast';
import type { InventarioItem } from '../types';

export function Inventario() {
  const { data: bodegas, isLoading: cargandoBodegas, isError: errorBodegas, error: eB } = useBodegas();
  const [bodegaId, setBodegaId] = useState('');
  const [ajuste, setAjuste] = useState<InventarioItem | null>(null);

  // Selecciona la bodega principal en cuanto llega la lista.
  useEffect(() => {
    if (!bodegaId && bodegas && bodegas.length) setBodegaId(idBodegaPrincipal(bodegas));
  }, [bodegas, bodegaId]);

  const { data: inv, isLoading: cargandoInv, isError: errorInv, error: eI, isFetching } = useInventario(bodegaId);
  const nombreBodega = bodegas?.find((b) => b.id === bodegaId)?.nombre ?? '';

  if (cargandoBodegas) {
    return (<><div className="page-head"><h2>Inventario</h2></div><SkeletonTable rows={6} /></>);
  }
  if (errorBodegas) {
    return (<><div className="page-head"><h2>Inventario</h2></div><ErrorBox mensaje={(eB as Error).message} /></>);
  }
  if (!bodegas || !bodegas.length) {
    return (
      <>
        <div className="page-head"><h2>Inventario</h2></div>
        <div className="card"><div className="empty-state"><i className="fa-solid fa-warehouse" />Crea una bodega para gestionar inventario.</div></div>
      </>
    );
  }

  const cols: Column<InventarioItem>[] = [
    {
      key: 'nombre', label: 'Ítem',
      render: (r) => (
        <div className="cell-item">
          <Thumb foto={r.foto} icon={r.tipo === 'presentacion' ? 'fa-boxes-stacked' : 'fa-box'} />
          <div><b>{r.nombre}</b><br /><small>{r.tipo === 'presentacion' ? 'Presentación' : 'Producto'}</small></div>
        </div>
      ),
    },
    { key: 'stock', label: 'Stock', render: (r) => (<><b style={{ fontSize: 15 }}>{r.stock}</b> <small className="text-muted">{r.unidad || ''}</small></>) },
    { key: 'stock_minimo', label: 'Mínimo', render: (r) => r.stock_minimo },
    { key: 'nivel', label: 'Nivel', render: (r) => <NivelBadge nivel={r.nivel} /> },
    {
      key: '_acc', label: '', sortable: false,
      render: (r) => (
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button className="btn ghost sm" onClick={() => setAjuste(r)}><i className="fa-solid fa-sliders" /> Ajustar</button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <h2>Inventario</h2>
        <div className="field" style={{ margin: 0, minWidth: 220 }}>
          <select className="control" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)} aria-label="Bodega">
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
      </div>

      {cargandoInv || isFetching ? (
        <SkeletonTable rows={6} />
      ) : errorInv ? (
        <ErrorBox mensaje={(eI as Error).message} />
      ) : (
        <DataTable title={`Existencias en ${nombreBodega}`} pageSize={10} cols={cols} rows={inv ?? []} empty="Esta bodega no tiene existencias todavía." />
      )}

      {ajuste ? <AjusteModal bodegaId={bodegaId} item={ajuste} onClose={() => setAjuste(null)} /> : null}
    </>
  );
}

function AjusteModal({ bodegaId, item, onClose }: { bodegaId: string; item: InventarioItem; onClose: () => void }) {
  const ajustar = useAjustarStock();
  const toast = useToast();
  const [stock, setStock] = useState(String(item.stock));
  const [motivo, setMotivo] = useState('');
  const [enviado, setEnviado] = useState(false);

  const guardar = async () => {
    setEnviado(true);
    if (stock === '' || Number(stock) < 0) return;
    try {
      await ajustar.mutateAsync({ bodegaId, tipo: item.tipo, refId: item.ref_id, stock: Number(stock), motivo });
      toast.success('Stock ajustado', item.nombre);
      onClose();
    } catch (e) {
      toast.error('No se pudo ajustar', (e as Error).message);
    }
  };

  return (
    <Modal
      open
      title={`Ajustar — ${item.nombre}`}
      icon="fa-sliders" iconColor="info"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className={`btn primary${ajustar.isPending ? ' loading' : ''}`} onClick={guardar}>Guardar ajuste</button>
        </>
      }
    >
      <p className="text-muted" style={{ marginTop: 0 }}>Stock actual: <b>{item.stock}</b>. Escribe el valor exacto que debe quedar registrado.</p>
      <Field name="stock" label="Nuevo stock" type="number" required min={0} value={stock} onChange={setStock}
        invalid={enviado && (stock === '' || Number(stock) < 0)} />
      <Field name="motivo" label="Motivo del ajuste" value={motivo} onChange={setMotivo} placeholder="Conteo físico, merma, corrección..." />
    </Modal>
  );
}
